"use client";

import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import type Konva from "konva";
import { Group, Image as KonvaImage, Layer, Rect, Stage, Text as KonvaText, Transformer } from "react-konva";
import type { PDFDocumentProxy, PDFPageProxy } from "pdfjs-dist/legacy/build/pdf.mjs";
import type { PdfEditorElement, PdfStickerRequest } from "./types";

type PdfEditorProps = {
  fileUrl: string;
  sessionId: string | null;
  onElementsChange?: (elements: PdfEditorElement[]) => void;
  onElementAdded?: (element: PdfEditorElement) => void;
  onSelectElement?: (element: PdfEditorElement | null) => void;
  onSelectionCountChange?: (count: number) => void;
};

export type PdfEditorHandle = {
  addSticker: (request: PdfStickerRequest) => void;
  updateElement: (elementId: string, patch: Partial<Pick<PdfEditorElement, "value" | "width" | "height" | "style">>) => void;
  deleteElement: (elementId: string) => void;
  deleteSelected: () => void;
};

type PageInfo = {
  widthPt: number;
  heightPt: number;
  canvasWidth: number;
  canvasHeight: number;
  scale: number;
};

const DEFAULT_TEXT_WIDTH = 180;
const DEFAULT_TEXT_HEIGHT = 32;
const DEFAULT_SIGNATURE_WIDTH = 180;
const DEFAULT_SIGNATURE_HEIGHT = 70;
const DEFAULT_MASK_WIDTH = 180;
const DEFAULT_MASK_HEIGHT = 64;
const MIN_ELEMENT_SIZE = 18;
const STACK_STEP_PT = 42;
const MIN_SELECTION_SIZE = 4;
const KEYBOARD_NUDGE_PT = 2;
const KEYBOARD_FAST_NUDGE_PT = 10;

let pdfWorkerConfigured = false;

type PdfJsModule = typeof import("pdfjs-dist/legacy/build/pdf.mjs");

const importPdfJs = new Function("specifier", "return import(specifier)") as (
  specifier: string,
) => Promise<PdfJsModule>;

async function configurePdfWorker() {
  if (pdfWorkerConfigured || typeof window === "undefined") {
    return;
  }

  const pdfjs = await importPdfJs("/pdf.mjs");
  pdfjs.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.mjs";
  pdfWorkerConfigured = true;
}

function toCanvasRect(element: PdfEditorElement, pageInfo: PageInfo) {
  return {
    x: element.x * pageInfo.scale,
    y: (pageInfo.heightPt - element.y - element.height) * pageInfo.scale,
    width: element.width * pageInfo.scale,
    height: element.height * pageInfo.scale,
  };
}

function toPdfRect(rect: { x: number; y: number; width: number; height: number }, pageInfo: PageInfo) {
  return {
    x: rect.x / pageInfo.scale,
    y: pageInfo.heightPt - (rect.y + rect.height) / pageInfo.scale,
    width: rect.width / pageInfo.scale,
    height: rect.height / pageInfo.scale,
  };
}

function normalizeRect(rect: { x: number; y: number; width: number; height: number }) {
  const x = rect.width < 0 ? rect.x + rect.width : rect.x;
  const y = rect.height < 0 ? rect.y + rect.height : rect.y;

  return {
    x,
    y,
    width: Math.abs(rect.width),
    height: Math.abs(rect.height),
  };
}

function intersectsRect(a: { x: number; y: number; width: number; height: number }, b: { x: number; y: number; width: number; height: number }) {
  return a.x <= b.x + b.width && a.x + a.width >= b.x && a.y <= b.y + b.height && a.y + a.height >= b.y;
}

function createElementId() {
  if (globalThis.crypto && "randomUUID" in globalThis.crypto) {
    return globalThis.crypto.randomUUID();
  }

  return `element-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

function sortElementsForLayering(elements: PdfEditorElement[]) {
  return [...elements].sort((a, b) => {
    if (a.type === "mask" && b.type !== "mask") {
      return -1;
    }
    if (a.type !== "mask" && b.type === "mask") {
      return 1;
    }
    return 0;
  });
}

function createElement(request: PdfStickerRequest, page: number, pageInfo: PageInfo, stackIndex: number): PdfEditorElement {
  const isSignature = request.type === "signature";
  const isMask = request.type === "mask";
  const width = isMask ? DEFAULT_MASK_WIDTH : isSignature ? DEFAULT_SIGNATURE_WIDTH : DEFAULT_TEXT_WIDTH;
  const height = isMask ? DEFAULT_MASK_HEIGHT : isSignature ? DEFAULT_SIGNATURE_HEIGHT : DEFAULT_TEXT_HEIGHT;
  const topOffset = 90 + stackIndex * STACK_STEP_PT;

  return {
    id: createElementId(),
    type: request.type,
    page,
    x: 40,
    y: Math.max(40, pageInfo.heightPt - topOffset),
    width,
    height,
    value: request.value,
    assetPath: request.assetPath,
    style: {
      fontSize: request.type === "date" ? 14 : 15,
    },
  };
}

function useLoadedImages(elements: PdfEditorElement[]) {
  const [images, setImages] = useState<Record<string, HTMLImageElement>>({});

  useEffect(() => {
    let cancelled = false;
    const signatureElements = elements.filter((element) => element.type === "signature" && element.assetPath);

    signatureElements.forEach((element) => {
      if (!element.assetPath || images[element.id]) {
        return;
      }

      const image = new window.Image();
      image.onload = () => {
        if (!cancelled) {
          setImages((current) => ({ ...current, [element.id]: image }));
        }
      };
      image.src = element.assetPath;
    });

    return () => {
      cancelled = true;
    };
  }, [elements, images]);

  return images;
}

export const PdfEditor = forwardRef<PdfEditorHandle, PdfEditorProps>(function PdfEditor(
  { fileUrl, sessionId, onElementAdded, onElementsChange, onSelectElement, onSelectionCountChange },
  ref,
) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const stageWrapRef = useRef<HTMLDivElement | null>(null);
  const transformerRef = useRef<Konva.Transformer | null>(null);
  const nodeRefs = useRef(new Map<string, Konva.Group>());

  const [pdfDocument, setPdfDocument] = useState<PDFDocumentProxy | null>(null);
  const [pdfPage, setPdfPage] = useState<PDFPageProxy | null>(null);
  const [pageInfo, setPageInfo] = useState<PageInfo | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [zoom, setZoom] = useState(1);
  const [elements, setElements] = useState<PdfEditorElement[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [selectionRect, setSelectionRect] = useState<{ x: number; y: number; width: number; height: number } | null>(null);
  const [status, setStatus] = useState("Ladowanie PDF...");
  const [hydratedSessionId, setHydratedSessionId] = useState<string | null>(null);
  const selectionStartRef = useRef<{ x: number; y: number } | null>(null);
  const dragSelectionRef = useRef<{
    activeId: string;
    startX: number;
    startY: number;
    initialElements: PdfEditorElement[];
    selectedIds: string[];
  } | null>(null);
  const images = useLoadedImages(elements);

  const visibleElements = useMemo(
    () => elements.filter((element) => element.page === currentPage),
    [currentPage, elements],
  );
  const selectedElement = useMemo(
    () => (selectedIds.length === 1 ? elements.find((element) => element.id === selectedIds[0]) || null : null),
    [elements, selectedIds],
  );

  const updateElements = useCallback(
    (nextElements: PdfEditorElement[]) => {
      const layeredElements = sortElementsForLayering(nextElements);
      setElements(layeredElements);
      onElementsChange?.(layeredElements);
    },
    [onElementsChange],
  );

  useEffect(() => {
    let cancelled = false;

    async function loadPdf() {
      setStatus("Ladowanie PDF...");
      setPdfDocument(null);
      setPdfPage(null);
      setPageInfo(null);
      setCurrentPage(1);
      setSelectedIds([]);

      await configurePdfWorker();
      const pdfjs = await importPdfJs("/pdf.mjs");
      const document = await pdfjs.getDocument(fileUrl).promise;

      if (!cancelled) {
        setPdfDocument(document);
        setStatus("");
      }
    }

    loadPdf().catch((error) => {
      console.error("PDF load failed", error);
      if (!cancelled) {
        setStatus("Nie udalo sie wyrenderowac PDF.");
      }
    });

    return () => {
      cancelled = true;
    };
  }, [fileUrl]);

  useEffect(() => {
    if (!sessionId) {
      return;
    }

    let cancelled = false;

    fetch(`/api/pdf/sessions/${sessionId}/elements`, { cache: "no-store" })
      .then((response) => (response.ok ? response.json() : { elements: [] }))
      .then((payload) => {
        if (!cancelled) {
          updateElements(payload.elements || []);
          setHydratedSessionId(sessionId);
        }
      })
      .catch(() => {
        if (!cancelled) {
          updateElements([]);
          setHydratedSessionId(sessionId);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [sessionId, updateElements]);

  useEffect(() => {
    if (!sessionId || hydratedSessionId !== sessionId) {
      return;
    }

    const timeout = window.setTimeout(() => {
      fetch(`/api/pdf/sessions/${sessionId}/elements`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ elements }),
      }).catch(() => undefined);
    }, 450);

    return () => window.clearTimeout(timeout);
  }, [elements, hydratedSessionId, sessionId]);

  useEffect(() => {
    let cancelled = false;

    async function renderPage() {
      if (!pdfDocument) {
        return;
      }

      const page = await pdfDocument.getPage(currentPage);
      const baseViewport = page.getViewport({ scale: 1 });
      const availableWidth = Math.max(320, (stageWrapRef.current?.clientWidth || 900) - 24);
      const fitScale = Math.min(1.8, availableWidth / baseViewport.width);
      const renderScale = fitScale * zoom;
      const viewport = page.getViewport({ scale: renderScale });
      const canvas = canvasRef.current;
      const context = canvas?.getContext("2d");

      if (!canvas || !context || cancelled) {
        return;
      }

      canvas.width = Math.floor(viewport.width);
      canvas.height = Math.floor(viewport.height);
      canvas.style.width = `${viewport.width}px`;
      canvas.style.height = `${viewport.height}px`;
      context.clearRect(0, 0, canvas.width, canvas.height);

      await page.render({ canvas, canvasContext: context, viewport }).promise;

      if (!cancelled) {
        setPdfPage(page);
        setPageInfo({
          widthPt: baseViewport.width,
          heightPt: baseViewport.height,
          canvasWidth: viewport.width,
          canvasHeight: viewport.height,
          scale: renderScale,
        });
      }
    }

    renderPage().catch((error) => {
      console.error("PDF page render failed", error);
      if (!cancelled) {
        setStatus("Nie udalo sie wyrenderowac strony PDF.");
      }
    });

    return () => {
      cancelled = true;
    };
  }, [currentPage, pdfDocument, zoom]);

  useEffect(() => {
    const transformer = transformerRef.current;
    if (!transformer) {
      return;
    }

    const selectedNodes = selectedIds.flatMap((id) => {
      const node = nodeRefs.current.get(id);
      return node ? [node] : [];
    });
    transformer.nodes(selectedNodes);
    transformer.getLayer()?.batchDraw();
    onSelectElement?.(selectedElement);
    onSelectionCountChange?.(selectedIds.length);
  }, [onSelectElement, onSelectionCountChange, selectedElement, selectedIds]);

  const updateElement = useCallback(
    (id: string, patch: Partial<PdfEditorElement>) => {
      updateElements(elements.map((element) => (element.id === id ? { ...element, ...patch } : element)));
    },
    [elements, updateElements],
  );

  useImperativeHandle(
    ref,
    () => ({
      addSticker(request) {
        if (!pageInfo || (request.type === "signature" && !request.assetPath)) {
          return;
        }

        const stackIndex = elements.filter((element) => element.page === currentPage && element.type !== "mask").length;
        const nextElement = createElement(request, currentPage, pageInfo, request.type === "mask" ? 0 : stackIndex);
        updateElements([...elements, nextElement]);
        setSelectedIds([nextElement.id]);
        setStatus("");
        onElementAdded?.(nextElement);
      },
      updateElement(elementId, patch) {
        updateElements(
          elements.map((element) =>
            element.id === elementId
              ? {
                  ...element,
                  ...patch,
                  style: {
                    ...element.style,
                    ...patch.style,
                  },
                }
              : element,
          ),
        );
      },
      deleteElement(elementId) {
        updateElements(elements.filter((element) => element.id !== elementId));
        if (selectedIds.includes(elementId)) {
          setSelectedIds(selectedIds.filter((id) => id !== elementId));
        }
      },
      deleteSelected() {
        if (selectedIds.length === 0) {
          return;
        }

        updateElements(elements.filter((element) => !selectedIds.includes(element.id)));
        setSelectedIds([]);
      },
    }),
    [currentPage, elements, onElementAdded, pageInfo, selectedIds, updateElements],
  );

  const deleteSelected = useCallback(() => {
    if (selectedIds.length === 0) {
      return;
    }

    updateElements(elements.filter((element) => !selectedIds.includes(element.id)));
    setSelectedIds([]);
  }, [elements, selectedIds, updateElements]);

  const moveSelectedByKeyboard = useCallback(
    (deltaX: number, deltaY: number) => {
      if (selectedIds.length === 0) {
        return;
      }

      updateElements(
        elements.map((element) =>
          selectedIds.includes(element.id)
            ? {
                ...element,
                x: element.x + deltaX,
                y: element.y + deltaY,
              }
            : element,
        ),
      );
    },
    [elements, selectedIds, updateElements],
  );

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      const target = event.target as HTMLElement | null;
      if (target?.tagName === "INPUT" || target?.tagName === "TEXTAREA") {
        return;
      }

      if ((event.key === "Delete" || event.key === "Backspace") && selectedIds.length > 0) {
        event.preventDefault();
        deleteSelected();
        return;
      }

      if (selectedIds.length > 0 && ["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"].includes(event.key)) {
        const step = event.shiftKey ? KEYBOARD_FAST_NUDGE_PT : KEYBOARD_NUDGE_PT;
        const deltaByKey: Record<string, [number, number]> = {
          ArrowLeft: [-step, 0],
          ArrowRight: [step, 0],
          ArrowUp: [0, step],
          ArrowDown: [0, -step],
        };
        const [deltaX, deltaY] = deltaByKey[event.key];

        event.preventDefault();
        moveSelectedByKeyboard(deltaX, deltaY);
      }
    }

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [deleteSelected, moveSelectedByKeyboard, selectedIds.length]);

  function getStagePointerPosition(event: Konva.KonvaEventObject<MouseEvent | TouchEvent>) {
    const stage = event.target.getStage();
    const position = stage?.getPointerPosition();

    return position || null;
  }

  function handleSelectionStart(event: Konva.KonvaEventObject<MouseEvent | TouchEvent>) {
    if (event.target !== event.target.getStage()) {
      return;
    }

    const position = getStagePointerPosition(event);
    if (!position) {
      return;
    }

    selectionStartRef.current = position;
    setSelectionRect({ x: position.x, y: position.y, width: 0, height: 0 });
  }

  function handleSelectionMove(event: Konva.KonvaEventObject<MouseEvent | TouchEvent>) {
    if (!selectionStartRef.current) {
      return;
    }

    const position = getStagePointerPosition(event);
    if (!position) {
      return;
    }

    setSelectionRect({
      x: selectionStartRef.current.x,
      y: selectionStartRef.current.y,
      width: position.x - selectionStartRef.current.x,
      height: position.y - selectionStartRef.current.y,
    });
  }

  function handleSelectionEnd() {
    const normalizedSelection = selectionRect ? normalizeRect(selectionRect) : null;
    selectionStartRef.current = null;
    setSelectionRect(null);

    if (!normalizedSelection || !pageInfo) {
      return;
    }

    if (normalizedSelection.width < MIN_SELECTION_SIZE || normalizedSelection.height < MIN_SELECTION_SIZE) {
      setSelectedIds([]);
      return;
    }

    const nextSelectedIds = visibleElements
      .filter((element) => intersectsRect(toCanvasRect(element, pageInfo), normalizedSelection))
      .map((element) => element.id);

    setSelectedIds(nextSelectedIds);
  }

  function moveSelectedElements(activeId: string, nextRect: { x: number; y: number; width: number; height: number }, pageInfo: PageInfo) {
    const dragState = dragSelectionRef.current;

    if (!dragState || dragState.activeId !== activeId || dragState.selectedIds.length <= 1) {
      updateElement(activeId, toPdfRect(nextRect, pageInfo));
      return;
    }

    const deltaPdfX = (nextRect.x - dragState.startX) / pageInfo.scale;
    const deltaPdfY = -(nextRect.y - dragState.startY) / pageInfo.scale;

    updateElements(
      elements.map((element) => {
        const initialElement = dragState.initialElements.find((initial) => initial.id === element.id);

        if (!initialElement || !dragState.selectedIds.includes(element.id)) {
          return element;
        }

        return {
          ...element,
          x: initialElement.x + deltaPdfX,
          y: initialElement.y + deltaPdfY,
        };
      }),
    );
  }

  const pageCount = pdfDocument?.numPages || 1;

  return (
    <div className="pdf-editor">
      <div className="pdf-editor-toolbar">
        <div className="pdf-editor-page-actions">
          <button type="button" className="secondary" disabled={currentPage <= 1} onClick={() => setCurrentPage((page) => page - 1)}>
            Poprzednia
          </button>
          <span>
            Strona {currentPage} / {pageCount}
          </span>
          <button
            type="button"
            className="secondary"
            disabled={currentPage >= pageCount}
            onClick={() => setCurrentPage((page) => page + 1)}
          >
            Nastepna
          </button>
        </div>
        <label className="pdf-editor-zoom">
          Zoom
          <input
            type="range"
            min="0.6"
            max="2"
            step="0.1"
            value={zoom}
            onChange={(event) => setZoom(Number(event.target.value))}
          />
          <b>{Math.round(zoom * 100)}%</b>
        </label>
        {selectedIds.length > 0 ? (
          <button type="button" className="secondary" onClick={deleteSelected}>
            Usun zaznaczone ({selectedIds.length})
          </button>
        ) : null}
      </div>

      <p className="pdf-editor-status" aria-live="polite" hidden={!status}>
        {status}
      </p>

      <div ref={stageWrapRef} className="pdf-editor-scroll">
        <div
          className="pdf-editor-page"
          style={{
            width: pageInfo?.canvasWidth || 0,
            height: pageInfo?.canvasHeight || 0,
          }}
        >
          <canvas ref={canvasRef} className="pdf-editor-canvas" />
          {pageInfo ? (
            <Stage
              className="pdf-editor-overlay"
              width={pageInfo.canvasWidth}
              height={pageInfo.canvasHeight}
              onMouseDown={handleSelectionStart}
              onMouseMove={handleSelectionMove}
              onMouseUp={handleSelectionEnd}
              onTouchEnd={handleSelectionEnd}
              onTouchMove={handleSelectionMove}
              onTouchStart={handleSelectionStart}
            >
              <Layer>
                {visibleElements.map((element) => {
                  const rect = toCanvasRect(element, pageInfo);
                  return (
                    <GroupNode
                      key={element.id}
                      element={element}
                      image={images[element.id]}
                      rect={rect}
                      selected={selectedIds.includes(element.id)}
                      setNode={(node) => {
                        if (node) {
                          nodeRefs.current.set(element.id, node);
                        } else {
                          nodeRefs.current.delete(element.id);
                        }
                      }}
                      onDragStart={(rect) => {
                        dragSelectionRef.current = {
                          activeId: element.id,
                          startX: rect.x,
                          startY: rect.y,
                          initialElements: elements,
                          selectedIds: selectedIds.includes(element.id) ? selectedIds : [element.id],
                        };
                      }}
                      onSelect={() => setSelectedIds(selectedIds.includes(element.id) ? selectedIds : [element.id])}
                      onChange={(nextRect) => moveSelectedElements(element.id, nextRect, pageInfo)}
                      onDragMove={(nextRect) => moveSelectedElements(element.id, nextRect, pageInfo)}
                      onDragEnd={() => {
                        dragSelectionRef.current = null;
                      }}
                    />
                  );
                })}
                {selectionRect ? (
                  <Rect
                    {...normalizeRect(selectionRect)}
                    fill="rgba(37,99,235,0.08)"
                    stroke="#1d4ed8"
                    strokeWidth={1}
                    dash={[6, 4]}
                    listening={false}
                  />
                ) : null}
                <Transformer
                  ref={transformerRef}
                  rotateEnabled={false}
                  boundBoxFunc={(oldBox, newBox) => {
                    if (newBox.width < MIN_ELEMENT_SIZE || newBox.height < MIN_ELEMENT_SIZE) {
                      return oldBox;
                    }
                    return newBox;
                  }}
                />
              </Layer>
            </Stage>
          ) : null}
        </div>
      </div>
      <span className="sr-only">{pdfPage ? `Wyrenderowano strone ${currentPage}` : ""}</span>
    </div>
  );
});

type GroupNodeProps = {
  element: PdfEditorElement;
  image?: HTMLImageElement;
  rect: { x: number; y: number; width: number; height: number };
  selected: boolean;
  setNode: (node: Konva.Group | null) => void;
  onDragEnd: () => void;
  onDragMove: (rect: { x: number; y: number; width: number; height: number }) => void;
  onDragStart: (rect: { x: number; y: number; width: number; height: number }) => void;
  onSelect: () => void;
  onChange: (rect: { x: number; y: number; width: number; height: number }) => void;
};

function GroupNode({ element, image, rect, selected, setNode, onDragEnd, onDragMove, onDragStart, onSelect, onChange }: GroupNodeProps) {
  return (
    <Group
      ref={setNode}
      x={rect.x}
      y={rect.y}
      width={rect.width}
      height={rect.height}
      draggable
      onClick={onSelect}
      onTap={onSelect}
      onDragStart={() => {
        onSelect();
        onDragStart(rect);
      }}
      onDragMove={(event) => {
        onDragMove({
          x: event.target.x(),
          y: event.target.y(),
          width: rect.width,
          height: rect.height,
        });
      }}
      onDragEnd={(event) => {
        onChange({
          x: event.target.x(),
          y: event.target.y(),
          width: rect.width,
          height: rect.height,
        });
        onDragEnd();
      }}
      onTransformEnd={(event) => {
        const node = event.target;
        const scaleX = node.scaleX();
        const scaleY = node.scaleY();
        node.scaleX(1);
        node.scaleY(1);
        onChange({
          x: node.x(),
          y: node.y(),
          width: Math.max(MIN_ELEMENT_SIZE, rect.width * scaleX),
          height: Math.max(MIN_ELEMENT_SIZE, rect.height * scaleY),
        });
      }}
    >
      <Rect
        width={rect.width}
        height={rect.height}
        fill={element.type === "mask" ? "#ffffff" : "transparent"}
        stroke={selected ? "#1d4ed8" : "#60a5fa"}
        strokeWidth={selected ? 2 : 1}
        dash={selected ? [] : [2, 4]}
        opacity={selected ? 1 : 0.72}
        cornerRadius={4}
      />
      {element.type === "mask" ? null : element.type === "signature" && image ? (
        <KonvaImage image={image} width={rect.width} height={rect.height} listening={false} />
      ) : (
        <KonvaText
          text={element.value || (element.type === "date" ? "Data" : "Tekst")}
          width={rect.width}
          height={rect.height}
          padding={6}
          fontSize={(element.style?.fontSize || 15) * (rect.height / Math.max(element.height, 1))}
          fill="#0f172a"
          verticalAlign="middle"
          listening={false}
        />
      )}
    </Group>
  );
}
